import { useState, useEffect } from "preact/hooks";
import { presets, default_settings } from "./settings/presets/preset_values";
import { settingsToHexatonScala } from "./settings/scale/parse-scale.js";
import { loadCustomPresets } from "./settings/presets/custom-presets";
import { PRESET_SKIP_KEYS } from "./persistence/settings-registry.js";
import { normalizeModulationHistory } from "./tuning/modulation-runtime.js";

export { PRESET_SKIP_KEYS };

// Scale-related keys to clear on reset (keeps output settings)
export const SCALE_KEYS_TO_CLEAR = [
  "scale",
  "scale_import",
  "note_names",
  "note_colors",
  "key_labels",
  "fundamental",
  "reference_degree",
  "equivSteps",
  "equivInterval",
  "rSteps",
  "drSteps",
  "hexSize",
  "rotation",
  "center_degree",
  // "midiin_anchor_note" excluded — hardware setting, persists across presets
  "spectrum_colors",
  "fundamental_color",
  "name",
  "description",
  "short_description",
];

export const clearScaleSettings = () => {
  SCALE_KEYS_TO_CLEAR.forEach((key) => sessionStorage.removeItem(key));
};

export const findPreset = (preset) => {
  for (let g of presets) {
    for (let p of g.settings) {
      if (p.name === preset) {
        return { ...p, scale_import: settingsToHexatonScala(p) };
      }
    }
  }
  return default_settings;
};

// Scale preset hexSize down on phone-sized screens, but not below 20.
// Use the short edge so iPhone landscape matches portrait behaviour.
export const scaleHexSizeForScreen = (hexSize) => {
  const size = hexSize || 42;
  const shortEdge =
    typeof window === "undefined"
      ? Infinity
      : Math.min(window.innerWidth || Infinity, window.innerHeight || Infinity);
  if (shortEdge <= 600 && size > 31) {
    return Math.max(20, Math.floor(size * 0.75));
  }
  return size;
};

function sessionIntOrFallback(key, fallback) {
  if (typeof sessionStorage === "undefined") return fallback;
  const raw = sessionStorage.getItem(key);
  if (raw === null) return fallback;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sessionIntFromAliases(keys, fallback) {
  for (const key of keys) {
    const value = sessionIntOrFallback(key, null);
    if (Number.isFinite(value)) return value;
  }
  return fallback;
}

function restorePersistentAnchorFields(fallback = {}) {
  return {
    midiin_anchor_note: sessionIntFromAliases(
      ["midiin_anchor_note", "midiin_central_degree", "lumatone_center_note"],
      fallback.midiin_anchor_note ?? fallback.midiin_central_degree ?? 60,
    ),
    midiin_anchor_channel: sessionIntFromAliases(
      ["midiin_anchor_channel", "lumatone_center_channel"],
      fallback.midiin_anchor_channel ?? 1,
    ),
  };
}

// HEJI anchors may be auto-derived from the current preset's tuning/labels.
// Reset them on preset load so an inferred anchor from the previous preset
// cannot survive the merge unless the incoming preset explicitly defines one.
export const mergePresetIntoSettings = (settings, preset) => {
 const persistentAnchorFallback = {
  midiin_anchor_note: settings.midiin_anchor_note ?? 60,
  midiin_anchor_channel: settings.midiin_anchor_channel ?? 1,
};

  return {
    ...settings,
    heji_anchor_ratio: "",
    heji_anchor_label: "",
    ...preset,
    ...restorePersistentAnchorFields(persistentAnchorFallback),
    controller_virtual_anchor_x: null,
    controller_virtual_anchor_y: null,
  };
};

// Fields that count as "edits" for dirty detection — same as PRESET_SKIP_KEYS.
const DIRTY_FIELDS = PRESET_SKIP_KEYS;

const snapshotOf = (s, modulationLibrary = []) => {
  const settingsSnap = {};
  for (const k of DIRTY_FIELDS) settingsSnap[k] = JSON.stringify(s[k]);
  return {
    settings: settingsSnap,
    modulationLibrary: JSON.stringify(normalizeModulationHistory(modulationLibrary, { zeroCounts: true })),
  };
};

const isDirty = (snap, s, modulationLibrary = []) => {
  if (!snap) return false;
  for (const k of DIRTY_FIELDS) {
    if (JSON.stringify(s[k]) !== snap.settings[k]) return true;
  }
  return (
    JSON.stringify(normalizeModulationHistory(modulationLibrary, { zeroCounts: true })) !==
    snap.modulationLibrary
  );
};

// localStorage key for the "restore on reload" preference
const PERSIST_ON_RELOAD_KEY = "hexatone_persist_on_reload";

/**
 * Manages preset state: active preset identity, dirty detection, and all
 * load/revert operations. Persists the active preset selection to sessionStorage
 * so it survives page refresh (when persistOnReload is enabled).
 *
 * @param {object}   settings         - Current app settings (initial value read on mount)
 * @param {function} setSettings      - Settings updater from useQuery
 * @param {object}   options
 * @param {object}   options.synthRef - Ref to the live synth (for prepare() on preset load)
 * @param {function} options.onUserInteraction - Called to mark the user as having interacted
 * @param {function} options.bumpImportCount - Bumps the scale reset token so TuneCell preview UI clears
 * @param {function} options.bumpPresetRuntimeReset - Forces a full musical-surface rebuild/reset
 *                                                    for sidebar preset refresh actions
 *                                               (required to start AudioContext)
 * @returns {{ activeSource, activePresetName, isPresetDirty,
 *             persistOnReload, setPersistOnReload,
 *             presetChanged, onLoadCustomPreset, onClearUserPresets,
 *             onRevertBuiltin, onRevertUser }}
 */
const usePresets = (
  settings,
  setSettings,
  {
    synthRef,
    onUserInteraction,
    bumpImportCount,
    bumpPresetRuntimeReset,
    currentModulationLibrary,
    setPresetModulationLibrary,
    onPresetModulationLibraryLoaded,
  },
) => {
  const [activeSource, setActiveSource] = useState(null);
  const [activePresetName, setActivePresetName] = useState(null);
  // Snapshot stored in state so updating it triggers a re-render and
  // isPresetDirty recalculates correctly.
  const [savedPresetSnapshot, setSavedPresetSnapshot] = useState(null);
  // Defaults to false (clean start on reload) — opt-in, stored in localStorage.
  const [persistOnReload, setPersistOnReloadState] = useState(
    () => localStorage.getItem(PERSIST_ON_RELOAD_KEY) === "true",
  );

  const setPersistOnReload = (value) => {
    localStorage.setItem(PERSIST_ON_RELOAD_KEY, String(value));
    setPersistOnReloadState(value);
  };

  // On mount: restore the previously active preset from sessionStorage,
  // unless the user has opted into a clean start on every reload.
  useEffect(() => {
    if (!persistOnReload) return;

    const savedSource = sessionStorage.getItem("hexatone_preset_source");
    const savedName = sessionStorage.getItem("hexatone_preset_name");

    if (!savedSource || !savedName) return;

    if (savedSource === "builtin") {
      setActiveSource("builtin");
      setActivePresetName(savedName);
      const presetData = findPreset(savedName);
      if (presetData) {
        const adjustedPreset = {
          ...presetData,
          hexSize: scaleHexSizeForScreen(presetData.hexSize),
        };
        const merged = mergePresetIntoSettings(settings, adjustedPreset);
        const savedLibrary = normalizeModulationHistory(presetData.modulation_library, { zeroCounts: true });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        setSettings(() => merged);
      }
    } else if (savedSource === "user") {
      const customPresets = loadCustomPresets();
      const preset = customPresets.find((p) => p.name === savedName);
      if (preset) {
        setActiveSource("user");
        setActivePresetName(preset.name);
        const adjustedPreset = {
          ...preset,
          hexSize: scaleHexSizeForScreen(preset.hexSize),
        };
        const merged = mergePresetIntoSettings(settings, adjustedPreset);
        const savedLibrary = normalizeModulationHistory(preset.modulation_library, { zeroCounts: true });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        setSettings(() => merged);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount-only: restores session preset on initial load; re-running on settings change would override user edits

  const presetChanged = async (e) => {
    const presetName = e.target.value;
    if (!presetName) return;
    // Mark user interaction immediately so sample/audio warmup can begin in the
    // current gesture turn, but do not await prepare() here — iOS select
    // controls can lose the first committed value if the handler blocks before
    // the controlled value/state update lands.
    onUserInteraction();
    setActiveSource("builtin");
    setActivePresetName(presetName);
    sessionStorage.setItem("hexatone_preset_source", "builtin");
    sessionStorage.setItem("hexatone_preset_name", presetName);
    const presetData = findPreset(presetName);
    const adjustedPreset = {
      ...presetData,
      hexSize: scaleHexSizeForScreen(presetData.hexSize),
    };
    const merged = mergePresetIntoSettings(settings, adjustedPreset);
    bumpImportCount?.();
    const savedLibrary = normalizeModulationHistory(presetData.modulation_library, { zeroCounts: true });
    setPresetModulationLibrary(savedLibrary);
    onPresetModulationLibraryLoaded?.(savedLibrary);
    setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
    setSettings(() => merged);
    synthRef.current?.prepare?.();
  };

  const onLoadCustomPreset = (preset) => {
    onUserInteraction();
    setActiveSource("user");
    setActivePresetName(preset.name || null);
    sessionStorage.setItem("hexatone_preset_source", "user");
    if (preset.name) {
      sessionStorage.setItem("hexatone_preset_name", preset.name);
    } else {
      sessionStorage.removeItem("hexatone_preset_name");
    }
    const adjustedPreset = {
      ...preset,
      hexSize: scaleHexSizeForScreen(preset.hexSize),
    };
    const merged = mergePresetIntoSettings(settings, adjustedPreset);
    bumpImportCount?.();
    const savedLibrary = normalizeModulationHistory(preset.modulation_library, { zeroCounts: true });
    setPresetModulationLibrary(savedLibrary);
    onPresetModulationLibraryLoaded?.(savedLibrary);
    setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
    setSettings(() => merged);
  };

  const onClearUserPresets = () => {
    const remaining = loadCustomPresets();
    setActiveSource(null);
    setActivePresetName(null);
    setPresetModulationLibrary([]);
    onPresetModulationLibraryLoaded?.([]);
    sessionStorage.removeItem("hexatone_preset_source");
    sessionStorage.removeItem("hexatone_preset_name");

    if (remaining.length > 0) {
      // Load the first remaining preset after the deleted one(s)
      const preset = remaining[0];
      setActiveSource("user");
      setActivePresetName(preset.name);
      sessionStorage.setItem("hexatone_preset_source", "user");
      sessionStorage.setItem("hexatone_preset_name", preset.name);
      const merged = mergePresetIntoSettings(settings, preset);
      const savedLibrary = normalizeModulationHistory(preset.modulation_library, { zeroCounts: true });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      setSettings(() => merged);
    } else {
      // No user presets remain — clear scale keys and start fresh
      clearScaleSettings();
      window.location.reload();
    }
  };

  const onRevertBuiltin = () => {
    onUserInteraction();
    if (activePresetName) {
      bumpPresetRuntimeReset?.();
      const presetData = findPreset(activePresetName);
      const adjustedPreset = {
        ...presetData,
        hexSize: scaleHexSizeForScreen(presetData.hexSize),
      };
      const merged = mergePresetIntoSettings(settings, adjustedPreset);
      bumpImportCount?.();
      const savedLibrary = normalizeModulationHistory(presetData.modulation_library, { zeroCounts: true });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      setSettings(() => merged);
    }
  };

  const onRevertUser = () => {
    onUserInteraction();
    if (activePresetName) {
      const saved = loadCustomPresets().find((p) => p.name === activePresetName);
      if (saved) {
        bumpPresetRuntimeReset?.();
        const adjustedPreset = {
          ...saved,
          hexSize: scaleHexSizeForScreen(saved.hexSize),
        };
        const merged = mergePresetIntoSettings(settings, adjustedPreset);
        bumpImportCount?.();
        const savedLibrary = normalizeModulationHistory(saved.modulation_library, { zeroCounts: true });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        setSettings(() => merged);
      }
    }
  };

  // Called by useSettingsChange when scale_divide fires — the user has
  // generated a new scale that is no longer tied to any loaded preset.
  const onUserScaleEdit = (name) => {
    setActiveSource("user");
    setActivePresetName(name || null);
    sessionStorage.setItem("hexatone_preset_source", "user");
    if (name) {
      sessionStorage.setItem("hexatone_preset_name", name);
    } else {
      sessionStorage.removeItem("hexatone_preset_name");
    }
    setSavedPresetSnapshot(null);
  };

  return {
    activeSource,
    activePresetName,
    isPresetDirty: isDirty(savedPresetSnapshot, settings, currentModulationLibrary),
    persistOnReload,
    setPersistOnReload,
    presetChanged,
    onLoadCustomPreset,
    onClearUserPresets,
    onRevertBuiltin,
    onRevertUser,
    onUserScaleEdit,
  };
};

export default usePresets;
